import { useEffect, useRef, useState } from "react";

import { type PopupProps, Popup as SemanticPopup } from "semantic-ui-react";

import {
  FocusStackContext,
  createFocusTrap,
  useFocusStack,
} from "./utils/FocusStack";

type PopupComponentProps = PopupProps & { trapFocus?: boolean };

const Popup = ({ trapFocus, ...props }: PopupComponentProps) => {
  const focusStack = useFocusStack();

  const popupRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(props.open ?? false);

  const isOpen = props.open !== undefined ? props.open : internalOpen;

  useEffect(() => {
    if (!trapFocus || !isOpen) {
      return;
    }

    const returnTo = document.activeElement as HTMLElement | null;
    let focusTrap: ReturnType<typeof createFocusTrap> | null = null;

    const frame = requestAnimationFrame(() => {
      const el = popupRef.current;
      focusTrap = createFocusTrap(el);
      focusStack.insertFocusTrap(focusTrap);
    });

    return () => {
      cancelAnimationFrame(frame);
      if (focusTrap) {
        focusStack.removeFocusTrap(focusTrap);
      }
      if (returnTo && document.activeElement !== returnTo) {
        returnTo.focus();
      }
    };
  }, [trapFocus, isOpen, focusStack]);

  return (
    <FocusStackContext.Provider value={focusStack}>
      <SemanticPopup
        role="dialog"
        aria-modal="false"
        mountNode={document.getElementById("semantic-modal-mount-node")}
        closeOnEscape
        ref={popupRef}
        {...props}
        onOpen={(e, data) => {
          setInternalOpen(true);
          props.onOpen?.(e, data);
        }}
        onClose={(e, data) => {
          setInternalOpen(false);
          props.onClose?.(e, data);
        }}
      />
    </FocusStackContext.Provider>
  );
};

Popup.Content = SemanticPopup.Content;

export { Popup };
